import React, { useEffect, useMemo, useState } from "react";
import type {
  CampaignSummary,
  CampaignUser,
} from "../types";

interface CampaignFilterModalProps {
  isOpen: boolean;
  onClose: () => void;
  onApply: (filters: Record<string, 'include' | 'exclude'>) => void;
  campaignOptions: CampaignSummary[];
  initialFilters: Record<string, 'include' | 'exclude'>;
  users: CampaignUser[];
  campaignNameById: Record<string, string>;
}

export const CampaignFilterModal: React.FC<CampaignFilterModalProps> = ({
  isOpen,
  onClose,
  onApply,
  campaignOptions,
  initialFilters,
  users,
  campaignNameById,
}) => {
  const [tempFilters, setTempFilters] = useState(initialFilters);

  // Reset when modal opens
  useEffect(() => {
    if (isOpen) {
      setTempFilters(initialFilters);
    }
  }, [isOpen, initialFilters]);

  // Calculate matching users count
  const matchingUsersCount = useMemo(() => {
    const selectedCampaigns = Object.keys(tempFilters);
    if (selectedCampaigns.length === 0) return users.length;

    return users.filter((user) => {
      return selectedCampaigns.every((campaignId) => {
        const mode = tempFilters[campaignId];
        const userReceivedCampaign =
          user.campaignId === campaignId ||
          user.campaignName === campaignNameById[campaignId] ||
          user.campaigns?.some((campaign) => campaign.campaignId === campaignId);

        return mode === 'include' ? userReceivedCampaign : !userReceivedCampaign;
      });
    }).length;
  }, [tempFilters, users, campaignNameById]);

  const activeConditionsCount = Object.keys(tempFilters).length;

  const handleToggleMode = (campaignId: string, newMode: 'include' | 'exclude' | null) => {
    setTempFilters((current) => {
      const next = { ...current };
      if (newMode === null) {
        delete next[campaignId];
      } else {
        next[campaignId] = newMode;
      }
      return next;
    });
  };

  const handleClearAll = () => {
    setTempFilters({});
  };

  const handleApply = () => {
    onApply(tempFilters);
    onClose();
  };

  const getLastSentDate = (campaignId: string): string | null => {
    // Find most recent sent date from users who received this campaign
    const dates = users
      .flatMap((user) => user.campaigns || [])
      .filter((c) => c.campaignId === campaignId && c.lastSentAt)
      .map((c) => new Date(c.lastSentAt!).getTime())
      .filter((t) => !isNaN(t));

    if (dates.length === 0) return null;

    const mostRecent = Math.max(...dates);
    return new Date(mostRecent).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40 bg-gray-900/30 backdrop-blur-sm"
        onClick={onClose}
      ></div>

      {/* Modal Container */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="relative w-full max-w-5xl rounded-3xl bg-white shadow-2xl">
          {/* Header */}
          <div className="flex items-start justify-between border-b border-gray-100 p-5">
            <div>
              <div className="text-lg font-semibold text-gray-900">
                Campaign Filter
              </div>
              <p className="text-xs text-gray-500">
                Filter users by campaigns they have or haven't received
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="rounded-full border border-gray-200 p-2 text-gray-500 transition hover:border-[var(--primary-color)] hover:text-[var(--primary-color)]"
              aria-label="Close campaign filter"
              style={{ cursor: 'pointer' }}
            >
              <svg className="h-4 w-4" viewBox="0 0 20 20" fill="none">
                <path d="m6 6 8 8M14 6l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </div>

          {/* Content */}
          <div className="max-h-[70vh] overflow-y-auto p-5">
            <div className="space-y-4">
              {/* Live Preview Badge */}
              <div className="rounded-2xl border border-blue-200 bg-blue-50 px-4 py-3">
                <div className="flex items-center gap-2">
                  <div>
                    <div className="text-sm font-semibold text-blue-900">
                      {matchingUsersCount} {matchingUsersCount === 1 ? 'user matches' : 'users match'} these conditions
                    </div>
                    <div className="text-xs text-blue-600">
                      {activeConditionsCount === 0
                        ? 'No filters applied - showing all users'
                        : `${activeConditionsCount} ${activeConditionsCount === 1 ? 'condition' : 'conditions'} active`}
                    </div>
                  </div>
                </div>
              </div>

              {/* Table */}
              <div className="overflow-hidden rounded-2xl border border-gray-200">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600">
                        Campaign
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600">
                        Last Sent
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wide text-gray-600">
                        Condition
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 bg-white">
                    {campaignOptions.length === 0 ? (
                      <tr>
                        <td colSpan={3} className="px-4 py-8 text-center text-sm text-gray-500">
                          No campaigns available
                        </td>
                      </tr>
                    ) : (
                      campaignOptions.map((campaign) => {
                        const mode = tempFilters[campaign.id];
                        const lastSent = getLastSentDate(campaign.id);

                        return (
                          <tr key={campaign.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3">
                              <div className="font-semibold text-gray-900">{campaign.name}</div>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600">
                              {lastSent || 'Never sent'}
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center justify-center gap-2">
                                <button
                                  type="button"
                                  onClick={() => handleToggleMode(campaign.id, 'include')}
                                  className={`rounded-lg border px-3 py-1.5 text-xs font-semibold transition ${
                                    mode === 'include'
                                      ? 'border-green-500 bg-green-500 text-white shadow-sm'
                                      : 'border-gray-200 bg-white text-gray-600 hover:border-green-500 hover:text-green-600'
                                  }`}
                                  style={{ cursor: 'pointer' }}
                                >
                                  Include
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleToggleMode(campaign.id, 'exclude')}
                                  className={`rounded-lg border px-3 py-1.5 text-xs font-semibold transition ${
                                    mode === 'exclude'
                                      ? 'border-red-500 bg-red-500 text-white shadow-sm'
                                      : 'border-gray-200 bg-white text-gray-600 hover:border-red-500 hover:text-red-600'
                                  }`}
                                  style={{ cursor: 'pointer' }}
                                >
                                  Exclude
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleToggleMode(campaign.id, null)}
                                  className={`rounded-lg border px-3 py-1.5 text-xs font-semibold transition ${
                                    mode === undefined
                                      ? 'border-gray-300 bg-gray-100 text-gray-700 shadow-sm'
                                      : 'border-gray-200 bg-white text-gray-500 hover:border-gray-300 hover:text-gray-700'
                                  }`}
                                  style={{ cursor: 'pointer' }}
                                >
                                  Skip
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              {/* Active Conditions Summary */}
              {activeConditionsCount > 0 && (
                <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
                  <div className="text-xs font-semibold uppercase tracking-wide text-gray-600 mb-2">
                    Active conditions ({activeConditionsCount})
                  </div>
                  <div className="space-y-1">
                    {Object.entries(tempFilters).map(([campaignId, mode]) => {
                      const name = campaignNameById[campaignId] || campaignId;
                      return (
                        <div key={campaignId} className="flex items-center gap-2 text-sm">
                          <span className={`inline-flex h-5 w-5 items-center justify-center rounded-full text-xs font-bold ${
                            mode === 'include'
                              ? 'bg-green-100 text-green-700'
                              : 'bg-red-100 text-red-700'
                          }`}>
                            {mode === 'include' ? '+' : '−'}
                          </span>
                          <span className="text-gray-700">
                            {mode === 'include' ? 'Users who HAVE received' : 'Users who have NOT received'}{' '}
                            <span className="font-semibold text-gray-900">"{name}"</span>
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between border-t border-gray-100 p-5">
            <button
              type="button"
              onClick={handleClearAll}
              disabled={activeConditionsCount === 0}
              className="rounded-full border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-600 transition hover:border-red-500 hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-40"
              style={{ cursor: activeConditionsCount === 0 ? 'not-allowed' : 'pointer' }}
            >
              Clear all
            </button>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={onClose}
                className="rounded-full border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-600 transition hover:border-[var(--primary-color)] hover:text-[var(--primary-color)]"
                style={{ cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleApply}
                className="rounded-full border border-gray-200 bg-[var(--primary-color)] px-5 py-2 text-sm font-semibold text-white transition hover:brightness-110"
                style={{ cursor: 'pointer' }}
              >
                Apply {activeConditionsCount > 0 && `(${activeConditionsCount})`}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
